import { Button } from '@ooh/ui/components/button';
import { TableCell, TableHead, TableHeader, TableRow } from '@ooh/ui/components/table';
import type { AvailabilityGridRow, AvailabilityPeriod } from '../lib/availability-types';
import { UnitIdentifierCell } from './unit-identifier-cell';

interface AvailabilityGridHeadProps {
  periods: AvailabilityPeriod[];
  showRowHeader: boolean;
  leftHeaderLabel: string;
  rangeStartLabel?: string;
  rangeEndLabel?: string;
  colWidth: number;
  edgeColWidth: number;
}

export function AvailabilityGridHead({
  periods,
  showRowHeader,
  leftHeaderLabel,
  rangeStartLabel,
  rangeEndLabel,
  colWidth,
  edgeColWidth,
}: AvailabilityGridHeadProps) {
  const hasRangeStart = rangeStartLabel !== undefined;
  const hasRangeEnd = rangeEndLabel !== undefined;
  return (
    <>
      <colgroup>
        {showRowHeader ? <col style={{ width: 180, minWidth: 180 }} /> : null}
        {hasRangeStart ? <col style={{ width: edgeColWidth, minWidth: edgeColWidth }} /> : null}
        {periods.map((period) => (
          <col key={period.id} style={{ width: colWidth, minWidth: colWidth }} />
        ))}
        {hasRangeEnd ? <col style={{ width: edgeColWidth, minWidth: edgeColWidth }} /> : null}
      </colgroup>
      <TableHeader className="sticky top-0 z-30">
        <TableRow>
          {showRowHeader ? (
            <TableHead className="sticky left-0 z-40 border border-border px-3 py-2 text-2xs font-semibold">
              {leftHeaderLabel}
            </TableHead>
          ) : null}
          {hasRangeStart ? (
            <TableHead
              data-testid="availability-edge-start"
              className="border border-border px-2 py-2 text-center text-2xs font-medium"
            >
              {rangeStartLabel}
            </TableHead>
          ) : null}
          {periods.map((period) => (
            <TableHead
              key={period.id}
              className="border border-border px-1 py-2 text-center text-2xs font-medium"
            >
              <span className="block truncate" title={period.label}>
                {period.label}
              </span>
            </TableHead>
          ))}
          {hasRangeEnd ? (
            <TableHead
              data-testid="availability-edge-end"
              className="border border-border px-2 py-2 text-center text-2xs font-medium"
            >
              {rangeEndLabel}
            </TableHead>
          ) : null}
        </TableRow>
      </TableHeader>
    </>
  );
}

export function AvailabilityRowHeaderCell({
  row,
  onUnitClick,
}: {
  row: AvailabilityGridRow;
  onUnitClick?: (unitId: string) => void;
}) {
  return (
    <TableCell className="sticky left-0 z-10 bg-white border border-border px-3 py-0 h-10 group-hover:bg-muted/30 transition-colors">
      <Button
        type="button"
        variant="ghost"
        className="block w-full min-w-0 h-auto p-0 text-left hover:bg-transparent"
        onClick={() => onUnitClick?.(row.id)}
      >
        <UnitIdentifierCell
          primary={row.label}
          secondary={row.secondaryLabel}
          testIdPrefix={`availability-row-${row.id}`}
        />
        {row.city ? (
          <span className="block truncate text-2xs text-muted-foreground" title={row.city}>
            {row.city}
          </span>
        ) : null}
      </Button>
    </TableCell>
  );
}
